import { useEffect, useState } from "react";
import styled from "styled-components";
import { GetHiringData } from "../Api/callData.js";

/* ──────────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Hero = styled.section`
  display: block;
  min-height: auto;
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(48px, 6vw, 72px);
  text-align: center;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, #eef2ff 0%, transparent 70%),
    #ffffff;
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 16px;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: clamp(40px, 5.8vw, 76px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.035em;
  color: #0f172a;
  margin: 0 0 20px;
`;

const HeroLead = styled.p`
  font-size: clamp(16px, 1.5vw, 19px);
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto 36px;
  max-width: 620px;
`;

const Tabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
`;

const Tab = styled.a`
  padding: 9px 22px;
  border-radius: 999px;
  font-size: 14.5px;
  font-weight: 500;
  text-decoration: none;
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "transparent"};
  box-shadow: ${(p) =>
    p.$active ? "0 6px 14px -6px rgba(99, 102, 241, 0.55)" : "none"};
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
`;

const Section = styled.section`
  display: block;
  min-height: auto;
  padding: clamp(64px, 8vw, 100px) 0;
  background: ${(p) => p.$bg || "#ffffff"};
  border-top: ${(p) => (p.$divider ? "1px solid #f1f5f9" : "none")};
`;

const SectionHeader = styled.div`
  margin-bottom: 56px;
  text-align: center;
`;

const Heading = styled.h2`
  font-size: clamp(28px, 3.6vw, 44px);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: #0f172a;
  margin: 0 0 16px;
`;

const Lead = styled.p`
  font-size: 17px;
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto;
  max-width: 600px;
`;

/* Ideal Talent grid */
const TalentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const TalentCard = styled.div`
  position: relative;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${(p) => p.$accent || "#c7d2fe"};
    transform: translateY(-3px);
    box-shadow: 0 24px 40px -24px ${(p) => p.$shadow || "rgba(99, 102, 241, 0.25)"};
  }

  .visual {
    aspect-ratio: 16 / 10;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(p) => p.$bg || "linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)"};
    color: ${(p) => p.$accent || "#6366f1"};
    position: relative;
    overflow: hidden;
  }
  .visual::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 70% 30%, rgba(255, 255, 255, 0.6), transparent 60%);
    pointer-events: none;
  }
  .visual svg {
    width: 64px;
    height: 64px;
    position: relative;
    z-index: 1;
  }
  .body {
    padding: 22px 24px 26px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.01em;
    line-height: 1.4;
  }
  p {
    font-size: 14px;
    line-height: 1.6;
    color: #64748b;
    margin: 0;
  }
`;

/* Open positions list */
const JobGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const JobCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 26px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #c7d2fe;
    transform: translateY(-2px);
    box-shadow: 0 18px 35px -18px rgba(15, 23, 42, 0.15);
  }
  &:hover .arrow {
    color: #6366f1;
    transform: translateX(2px);
  }

  .row1 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .title {
    font-size: 17px;
    font-weight: 600;
    color: #0f172a;
    letter-spacing: -0.01em;
    line-height: 1.4;
    flex: 1;
  }
  .arrow {
    color: #cbd5e1;
    transition: color 0.15s ease, transform 0.15s ease;
    flex-shrink: 0;
  }
  .meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 13.5px;
    color: #64748b;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: ${(p) => (p.$open ? "#ecfdf5" : "#f1f5f9")};
  color: ${(p) => (p.$open ? "#047857" : "#64748b")};
  border: 1px solid ${(p) => (p.$open ? "#a7f3d0" : "#e2e8f0")};

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(p) => (p.$open ? "#10b981" : "#94a3b8")};
  }
`;

const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 15px;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid #e2e8f0;
  border-top-color: #6366f1;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const AdminBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 18px;
    border-radius: 999px;
    background: #0f172a;
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: background 0.15s ease;
  }
  a:hover {
    background: #1e293b;
  }
`;

const ArrowRight = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const IconRocket = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </svg>
);
const IconHeartHands = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const IconTrendUp = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M23 6l-9.5 9.5-5-5L1 18" />
    <path d="M17 6h6v6" />
  </svg>
);

const talents = [
  {
    Icon: IconRocket,
    title: "Takes on challenges",
    desc: "Curious by default. Comfortable owning hard problems and pushing through ambiguity.",
    bg: "linear-gradient(135deg, #eef2ff 0%, #ede9fe 100%)",
    accent: "#6366f1",
    shadow: "rgba(99, 102, 241, 0.25)",
  },
  {
    Icon: IconHeartHands,
    title: "Collaborates with care",
    desc: "Builds trust, listens well, and treats teammates and users with respect.",
    bg: "linear-gradient(135deg, #fdf2f8 0%, #faf5ff 100%)",
    accent: "#a855f7",
    shadow: "rgba(168, 85, 247, 0.25)",
  },
  {
    Icon: IconTrendUp,
    title: "Grows continuously",
    desc: "Reflects, learns, and gets better — sees feedback as a gift, not a verdict.",
    bg: "linear-gradient(135deg, #ecfdf5 0%, #f0fdfa 100%)",
    accent: "#10b981",
    shadow: "rgba(16, 185, 129, 0.25)",
  },
];

export function Hiring() {
  const [post, setPost] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await GetHiringData();
      setPost(data || []);
      setLoading(false);
    };
    load();
  }, []);

  const isLoggedIn = typeof window !== "undefined" && localStorage.length > 0;

  return (
    <Page>
      <Hero>
        <Container>
          <Eyebrow>Community</Eyebrow>
          <HeroTitle>Recruitment</HeroTitle>
          <HeroLead>
            Join YoungAnd&apos;s journey — we&apos;re looking for talented
            people who want to build cognitive wellness solutions that matter.
          </HeroLead>
          <Tabs>
            <Tab href="/community/inform">Inform</Tab>
            <Tab href="/community/news">News</Tab>
            <Tab href="/community/hiring" $active>
              Recruitment
            </Tab>
          </Tabs>
        </Container>
      </Hero>

      <Section $divider>
        <Container>
          <SectionHeader>
            <Eyebrow>Ideal talent</Eyebrow>
            <Heading>The people we hire for.</Heading>
            <Lead>
              We look for teammates who bring curiosity, care, and a sense of
              ownership to everything they build.
            </Lead>
          </SectionHeader>

          <TalentGrid>
            {talents.map((t) => (
              <TalentCard
                key={t.title}
                $bg={t.bg}
                $accent={t.accent}
                $shadow={t.shadow}
              >
                <div className="visual">
                  <t.Icon />
                </div>
                <div className="body">
                  <h3>{t.title}</h3>
                  <p>{t.desc}</p>
                </div>
              </TalentCard>
            ))}
          </TalentGrid>
        </Container>
      </Section>

      <Section $bg="#fafafa" $divider>
        <Container>
          <SectionHeader>
            <Eyebrow>Open positions</Eyebrow>
            <Heading>We&apos;re hiring.</Heading>
            <Lead>
              Browse open roles below. Don&apos;t see your fit? Get in touch —
              we&apos;re always meeting great people.
            </Lead>
          </SectionHeader>

          {loading ? (
            <StateBox>
              <Spinner />
              <div>Loading positions…</div>
            </StateBox>
          ) : post && post.length > 0 ? (
            <>
              <JobGrid>
                {post.map((p) => {
                  const open =
                    p.expire_time && p.expire_time.toDate() >= new Date();
                  return (
                    <JobCard key={p.id} href={`/community/hiring/${p.id}`}>
                      <div className="row1">
                        <div className="title">{p.title}</div>
                        <span className="arrow" aria-hidden>
                          <ArrowRight />
                        </span>
                      </div>
                      <div className="meta">
                        <Badge $open={open}>
                          {open ? "Open" : "Closed"}
                        </Badge>
                        <span>
                          {p.reg_time
                            ? p.reg_time.toDate().toISOString().split("T")[0]
                            : ""}
                        </span>
                      </div>
                    </JobCard>
                  );
                })}
              </JobGrid>

              {isLoggedIn && (
                <AdminBar>
                  <a href="/adminWrite">Post a role</a>
                </AdminBar>
              )}
            </>
          ) : (
            <StateBox>
              <div style={{ fontSize: 36, lineHeight: 1 }}>💼</div>
              <div>No open positions right now.</div>
              <div style={{ fontSize: 13, color: "#cbd5e1" }}>
                Check back soon, or reach out at info@youngandx.com.
              </div>
            </StateBox>
          )}
        </Container>
      </Section>
    </Page>
  );
}
